import React from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRightIcon,
  ChevronRightIcon,
  InformationCircleIcon,
  CurrencyDollarIcon 
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';

/**
 * Logged Out Navigation State
 * 
 * Shows "I want to..." prompt with three audience paths:
 * 1. Consumers → Find Local Deals
 * 2. Business Owners → Advertise My Business
 * 3. Bar/Restaurant Owners → Get Free Coasters
 */
export default function MobileNavLoggedOut({ onClose }) {
  const { openAuthModal } = useAuth();

  const handleLoginClick = () => {
    onClose();
    openAuthModal('login');
  };

  return (
    <>
      {/* Prompt */}
      <div className="text-center mb-5">
        <div className="text-white/40 text-sm mb-0.5">What brings you here?</div>
        <div className="font-display text-2xl text-white">I want to...</div>
      </div>
      
      {/* Primary CTA: Consumers */}
      <Link
        to="/local-deals-near-me"
        onClick={onClose}
        className="block rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 p-4 mb-4 shadow-xl shadow-orange-500/30 hover:shadow-orange-500/40 transition-shadow"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
            <span className="text-2xl">🎁</span>
          </div>
          <div className="flex-1">
            <div className="font-display text-lg text-white">Find Local Deals</div>
            <div className="text-sm text-white/80">Save money at nearby businesses</div>
          </div>
          <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
            <ArrowRightIcon className="w-4 h-4 text-white" />
          </div>
        </div>
      </Link>
      
      {/* Quick Links */}
      <div className="flex items-center justify-center gap-4 mb-5">
        <Link
          to="/how-it-works"
          onClick={onClose}
          className="flex items-center gap-1.5 py-2 text-white/50 hover:text-white/70 transition-colors"
        >
          <InformationCircleIcon className="w-4 h-4" />
          <span className="text-sm">How It Works</span>
        </Link>
        <span className="text-white/20">•</span>
        <Link
          to="/our-story"
          onClick={onClose}
          className="flex items-center gap-1.5 py-2 text-white/50 hover:text-white/70 transition-colors"
        >
          <span className="text-sm">📖</span>
          <span className="text-sm">Our Story</span>
        </Link>
      </div>
      
      {/* Divider */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1 h-px bg-gradient-to-r from-transparent to-white/20" />
        <span className="text-xs text-white/40 uppercase tracking-wider font-medium">
          Partner With Us
        </span>
        <div className="flex-1 h-px bg-gradient-to-l from-transparent to-white/20" />
      </div>
      
      {/* Partner Options */}
      <div className="space-y-3 mb-6">
        {/* For Business Owners */}
        <Link
          to="/for-businesses"
          onClick={onClose}
          className="group block rounded-xl bg-gradient-to-r from-purple-500/10 to-violet-500/10 border border-purple-500/30 p-4 hover:border-purple-500/50 hover:bg-purple-500/15 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-purple-500 to-violet-500 rounded-lg flex items-center justify-center shadow-lg">
              <span className="text-lg">📈</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-white">Advertise My Business</div>
              <div className="text-xs text-white/50">Get more local customers</div>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-white/30 group-hover:text-white/50 transition-colors" />
          </div>
        </Link>
        
        {/* For Bars & Restaurants */}
        <Link
          to="/for-venues"
          onClick={onClose}
          className="group block rounded-xl bg-gradient-to-r from-cyan-500/10 to-teal-500/10 border border-cyan-500/30 p-4 hover:border-cyan-500/50 hover:bg-cyan-500/15 transition-all"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-gradient-to-br from-cyan-500 to-teal-500 rounded-lg flex items-center justify-center shadow-lg">
              <span className="text-lg">🍺</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-white">Get Free Coasters</div>
              <div className="text-xs text-white/50">I own a bar or restaurant</div>
            </div>
            <ChevronRightIcon className="w-5 h-5 text-white/30 group-hover:text-white/50 transition-colors" />
          </div>
        </Link>
      </div>
      
      {/* Footer Actions */}
      <div className="space-y-3">
        {/* Pricing link */}
        <Link
          to="/pricing"
          onClick={onClose}
          className="flex items-center justify-center gap-2 py-2 text-white/40 hover:text-white/60 transition-colors"
        >
          <CurrencyDollarIcon className="w-4 h-4" />
          <span className="text-sm">View full pricing details</span>
        </Link>
        
        {/* Log In */}
        <button
          onClick={handleLoginClick}
          className="block w-full py-3.5 rounded-xl border border-white/20 text-center text-white font-medium hover:bg-white/5 transition-colors"
        >
          Log In
        </button>
        
        {/* Sign Up */}
        <button
          onClick={() => {
            onClose();
            openAuthModal('signup');
          }}
          className="block w-full py-3.5 rounded-xl bg-gradient-to-r from-orange-500 to-amber-500 text-center text-white font-semibold hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg shadow-orange-500/20"
        >
          Sign Up Free
        </button>
      </div>
    </>
  );
}
